// src/features/services/ttlock/components/table_superadmin_rooms.tsx
import React from 'react';
import { getCookie } from '@/lib/http/bffClient';
import { useMutation } from '@tanstack/react-query';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import type { Lock } from '../api/locks';
import AppDialog_CreateRoom from './AppDialog_CreateRoom'; // ← adjust if needed

/** Shape returned by BFF GET /bff/ttlock/rooms (keys come from data_structure.yml) */
export type Room = {
  id_scope: number;
  room_name?: string | null;
  lock_id?: string | number | null;
  is_active?: boolean;
  is_available?: boolean;
  notes?: string | null;
  /** Prefer created_at; fall back to updated_at until backend sends created_at */
  created_at?: number | string | null;
  updated_at?: number | string | null;
};

type Props = {
  rooms: Room[];
  locks?: Lock[];                 // optional: to show “Lock Name (alias)”
  loading?: boolean;
  error?: unknown;
  onRefresh?: () => void;         // called after delete / create
  onNew?: () => void;             // optional override; if omitted we open the dialog here
};

/* cookie helper imported above */

/* inline trash icon (24px, hover red) */
function TrashIcon({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

export default function TableSuperadminRooms({
  rooms,
  locks = [],
  loading = false,
  error,
  onRefresh,
  onNew,
}: Props) {
  // ── New dialog state (like cards table) ──────────────────────────────
  const [createOpen, setCreateOpen] = React.useState(false);
  const handleNewClick = React.useCallback(() => {
    // allow parent override (keeps backward-compat)
    if (onNew) return onNew();
    setCreateOpen(true);
  }, [onNew]);

  // quick map lockId → alias/name
  const lockNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const l of locks) {
      const id = String(l.lockId);
      const name = l.lockAlias ?? l.lockName ?? '';
      m.set(id, name);
    }
    return m;
  }, [locks]);

  // delete (mark removed=true)
  const removeRoom = useMutation({
    mutationFn: async (id_scope: number) => {
      const csrf = getCookie('csrf');
      const res = await fetch(`/bff/ttlock/rooms/${id_scope}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: csrf ? { 'X-CSRF': csrf, accept: 'application/json' } : { accept: 'application/json' },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `DELETE /bff/ttlock/rooms/${id_scope} → ${res.status}`);
      }
      return true;
    },
    onSuccess: () => { onRefresh?.(); },
  });

  const onClickDelete = (r: Room) => {
    if (!r?.id_scope) return;
    const name = r.room_name ? ` (${r.room_name})` : '';
    if (window.confirm(`Remove room #${r.id_scope}${name}?`)) {
        removeRoom.mutate(r.id_scope);
      }
  };

  const columns = React.useMemo<ColumnDef<Room>[]>(() => ([
    {
      header: <span className="text-neutral-300">Name</span>,
      accessor: 'room_name',
      Cell: (v: string | null) => <span className="text-neutral-300">{v ?? '—'}</span>,
    },
    {
      header: <span className="text-neutral-300">Lock Name</span>,
      accessor: 'lock_id',
      Cell: (v: string | number | null) => {
        const key = v == null ? '' : String(v);
        const alias = key ? lockNameById.get(key) : undefined;
        return <span className="text-neutral-300">{alias || '—'}</span>;
      },
    },
    {
      header: <span className="text-neutral-300">Lock ID</span>,
      accessor: 'lock_id',
      Cell: (v: string | number | null) => <span className="text-neutral-300">{v ?? '—'}</span>,
    },
    {
      header: <span className="text-neutral-300">Created</span>,
      accessor: 'created_at', // may be undefined; we’ll fall back to updated_at below
      Cell: (_v: number | string | null | undefined, row) => {
        const raw = row.created_at ?? row.updated_at;
        if (!raw) return <span className="text-neutral-500">—</span>;
        const ms = typeof raw === 'number' ? raw : Date.parse(String(raw));
        const d  = Number.isFinite(ms) ? new Date(ms) : null;
        return <span className="text-neutral-400">{d ? d.toLocaleString() : '—'}</span>;
      },
    },
    {
      header: <span className="sr-only">Actions</span>,
      accessor: 'id_scope',
      Cell: (_v, row) => {
        const isBusy = removeRoom.isPending && removeRoom.variables === row.id_scope;
        return (
          <div className="w-full flex items-center justify-end">
            <button
              title="Remove room"
              disabled={isBusy}
              onClick={(e) => { e.stopPropagation(); onClickDelete(row); }}
              className={[
                'p-1 rounded transition-colors',
                isBusy
                  ? 'opacity-60 cursor-not-allowed'
                  : 'text-neutral-400 hover:text-rose-500',
              ].join(' ')}
            >
              <TrashIcon className="w-6 h-6" />
            </button>
          </div>
        );
      },
    },
  ]), [lockNameById, removeRoom.isPending, removeRoom.variables]);

  if (loading) return <p className="text-neutral-400">Loading rooms…</p>;
  if (error)   return <p className="text-rose-400">Failed to load rooms.</p>;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-neutral-400">{rooms.length} room{rooms.length === 1 ? '' : 's'}</span>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="px-2 py-1 text-xs rounded bg-neutral-800 hover:bg-neutral-700 text-neutral-300"
          >
            Refresh
          </button>
        )}
      </div>

      <MinimalisticTable<Room>
        columns={columns}
        data={rooms}
        noDataMessage="No rooms to show yet."
        showNewButton
        onNew={handleNewClick}
        showSearch
        searchPlaceholder="Filter rooms…"
        isRowClickable={() => false}
      />

      {/* --- Create dialog (mirrors cards table pattern) --- */}
    <AppDialog_CreateRoom
    open={createOpen}
    locks={locks}
    onOpenChange={setCreateOpen}
    onCreated={() => { setCreateOpen(false); onRefresh?.(); }}
    />
    </div>
  );
}
